import { actionCreator, getLocale } from '../../../helpers/utils';
import Resources from '../../../helpers/resources';


import { PRODUCT_IDS } from '../../../helpers/enums';

// SETTERS
export const SET_COURSE_COUNTRIES = 'SET_COURSE_COUNTRIES';
export const SET_COURSE_YEAR = 'SET_COURSE_YEAR';
export const SET_COURSE = 'SET_COURSE';
export const SET_UPF_COURSE = 'SET_UPF_COURSE';
export const SET_NEW_COURSE = 'SET_NEW_COURSE';
// GETTERS
export const GET_DEFAULT_COURSE = 'GET_DEFAULT_COURSE';
export const GET_COURSE_COUNTRIES = 'GET_COURSE_COUNTRIES';
export const GET_COURSE_YEARS = 'GET_COURSE_YEARS';
export const GET_COURSE = 'GET_COURSE';
export const GET_UPF_COURSE = 'GET_UPF_COURSE';
export const GET_NEW_COURSE = 'GET_NEW_COURSE';
export const RESET_COURSE_YEARS = 'RESET_COURSE_YEARS';

export const INITIALISE_COURSE_SELECT_OPTIONS = 'INITIALISE_COURSE_SELECT_OPTIONS';
export const GET_NEW_COURSE_AVAILABILITY_CRITERIA = 'GET_NEW_COURSE_AVAILABILITY_CRITERIA';

export const FETCHING_COURSE_CONTENT = 'FETCHING_COURSE_CONTENT';

/**
 * NOTE: Courses are not localised - i.e. we get courses based on country but the names are not localised
 * If in the future we localise them, we should pass the locale in the header for the course APIs
 */

const setCountryHandler = value => async (dispatch) => {
    const getCourseCountriesAction = actionCreator(SET_COURSE_COUNTRIES, { data: value });
    dispatch(getCourseCountriesAction);
};

const setYearHandler = value => async (dispatch) => {
    const getCourseYearAction = actionCreator(SET_COURSE_YEAR, { data: value });
    dispatch(getCourseYearAction);
};

const setCourseHandler = value => async (dispatch) => {
    const getCourseAction = actionCreator(SET_COURSE, { data: value });
    dispatch(getCourseAction);
};

const setUPFCourseHandler = value => async (dispatch) => {
    const getUPFCourseAction = actionCreator(SET_UPF_COURSE, { data: value });
    dispatch(getUPFCourseAction);
};

const setNewCourseHandler = value => async (dispatch) => {
    const setNewCourseAction = actionCreator(SET_NEW_COURSE, { data: value });
    dispatch(setNewCourseAction);
};

// To get default courses when we click ADD NEW GROUP button
/**
 * prerequisite:
 * @param productId
 * @param classId
 */
const getDefaultCourse = ({ productId, classId }) => async (dispatch) => {
    if (Resources.getDefaultCourse) {
        const getCourseResponse = await Resources.getDefaultCourse
            .get({
                classId,
                productId
            })
            .withParams({
                includeGlobalCourses: true
            })
            .headers({ Locale: getLocale() })
            .send();

        if (getCourseResponse.valid) {
            // To display rest of the data in select box if any one of the data(Country/Year/Course)
            // is missing from BE

            const {
                data: {
                    groupCourse,
                    defaultCountry,
                    defaultYear,
                    defaultCourse,
                    upfCourses,
                    conquestCourses,
                    countries,
                    yearGroups,
                    courses
                }
            } = getCourseResponse;

            const countryCode = defaultCountry.code !== null ? defaultCountry.code : '';
            const year = defaultYear !== null ? defaultYear.yearGroupEnum.toString() : '';
            const course = defaultCourse !== null ? defaultCourse.id.toString() : '';
            const upfCourse =
                productId === PRODUCT_IDS.Mathletics && upfCourses.length > 0 ? upfCourses[0].id.toString() : '';
            const newCourse = conquestCourses.length > 0 ? (conquestCourses.find(c => c.isDefault) || {}).id || '' : '';

            const getCourseCountriesAction = actionCreator(GET_DEFAULT_COURSE, {
                countryCode,
                countryList: countries,
                course,
                courseList: courses,
                currentCourse: groupCourse,
                upfCourse,
                upfCourseList: productId === PRODUCT_IDS.Mathletics ? upfCourses : '',
                newCourse,
                newCourseList: conquestCourses,
                year,
                yearList: yearGroups
            });

            dispatch(getCourseCountriesAction);
        }

        return getCourseResponse;
    }

    return false;
};

/**
 * prerequisite:
 * - this action must be followed by the "getCourseCountries" in order for the countryCode to be populated
 * @param countryCode
 */
const getCourseYears = ({ countryCode }) => async (dispatch) => {
    if (Resources.getCourseYears) {
        let getCourseResponse = Resources.getCourseYears
            .get()
            .headers({ Locale: getLocale() });

        /**
         * If there's countryCode available send it with the countryCode,
         * otherwise send it without a country code (applicable for school courses
         */
        if (countryCode) {
            getCourseResponse = await getCourseResponse
                .withParams({
                    countryCode
                })
                .headers({ Locale: getLocale() })
                .send();
        } else {
            getCourseResponse = await getCourseResponse.send();
        }

        if (getCourseResponse.valid) {
            const getCourseYearsAction = actionCreator(GET_COURSE_YEARS, {
                data: getCourseResponse.data
            });
            dispatch(getCourseYearsAction);
        }
    }
};

/**
 * prerequisite:
 *      - this action must be followed by the "getCourseCountries" AND "getCourseYears" in order for
 *      both the countryCode and yearGroup to be populated
 * @param countryCode
 * @param yearGroup
 * @param productId
 * @param noCourseText
 */
const getCourses = ({ countryCode, yearGroup, productId, noCourseText }) => async (dispatch) => {
    if (Resources.getCourses) {
        // If there's no country code, retrieve the schoolCourses endpoint instead
        const isSchoolCourse = !(countryCode !== '' && countryCode !== undefined);
        const response = isSchoolCourse ?
            Resources.getSchoolCourses
                .get()
                .withParams({
                    yearGroup,
                    productId,
                    includeGlobalCourses: true
                })
            :
            Resources.getCourses
                .get()
                .withParams({
                    countryCode,
                    yearGroup,
                    productId,
                    includeGlobalCourses: true
                });
        const getCourseResponse = await response.send();

        if (getCourseResponse.valid) {
            const getCourseAction = actionCreator(GET_COURSE, {
                data: getCourseResponse.data
            });
            dispatch(getCourseAction);

            // when isSchoolCourse is true, countryCode will be undefined. Need to pass groupCountryCode in this case
            // TODO how to pass groupCountryCode? through state?
            const getNewCourseAction = actionCreator(GET_NEW_COURSE, {
                data: {
                    ...getCourseResponse.data,
                    isSchoolCourse
                }
            });
            dispatch(getNewCourseAction);

            const getUPFCourseAction = actionCreator(GET_UPF_COURSE, {
                data: getCourseResponse.data
            });
            dispatch(getUPFCourseAction);
        }

        if (getCourseResponse.status === 404) {
            const getCourseAction = actionCreator(GET_COURSE, {
                data: {
                    id: '',
                    name: noCourseText
                },
                notFound: true
            });
            dispatch(getCourseAction);
        }
    }
};

const resetYearOptions = () => async (dispatch) => {
    const getCourseAction = actionCreator(RESET_COURSE_YEARS);
    dispatch(getCourseAction);
};

const initialiseCourseSelectOptions = ({
    countryOption,
    yearOption,
    allYearsOption,
    courseOption,
    upfCourseOption,
    newCourseOption,
    noNewCourseFoundOption,
    noUPFCourseFoundOption,
    upfCourseAdminSelectionOption,
    upfCurrentCourseSuffix
}) => async (dispatch) => {
    const teacherAction = actionCreator(INITIALISE_COURSE_SELECT_OPTIONS, {
        data: {
            countryOption,
            yearOption,
            allYearsOption,
            courseOption,
            upfCourseOption,
            newCourseOption,
            noNewCourseFoundOption,
            noUPFCourseFoundOption,
            upfCourseAdminSelectionOption,
            upfCurrentCourseSuffix
        }
    });
    dispatch(teacherAction);
};
// Retrieve the New Courses availability criteria i.e. if new courses are available for a range of year groups
// defined in config. New courses will be rolled out progressively for various countries and grades.
// The config is to be updated as and when new courses are available for a year group / country
const getNewCoursesAvailabilityCriteria = () => async (dispatch) => {
    if (Resources.getConfigurationItem) {
        const setConfig = (config) => {
            const { yearGroups = [], countryCodes = [] } = config || {};
            const configAction = actionCreator(GET_NEW_COURSE_AVAILABILITY_CRITERIA, {
                newCoursesAvailabilityCriteria: { yearGroups, countryCodes }
            });
            dispatch(configAction);
        };

        Resources.getConfigurationItem('newCoursesAvailabilityCriteria')
            .then(setConfig);
    }
};

export default {
    setCountryHandler,
    getDefaultCourse,
    setYearHandler,
    getCourseYears,
    setCourseHandler,
    setUPFCourseHandler,
    setNewCourseHandler,
    getCourses,
    resetYearOptions,
    initialiseCourseSelectOptions,
    getNewCoursesAvailabilityCriteria
};



// WEBPACK FOOTER //
// ./containers/console/actions/courses-actions.js